import type { BetterAuthPlugin } from 'better-auth'
import type { BetterAuthClientOptions } from 'better-auth/client'
import {
  adminClient,
  emailOTPClient,
  inferAdditionalFields,
  inferOrgAdditionalFields,
  jwtClient,
  lastLoginMethodClient,
  organizationClient,
  phoneNumberClient,
} from 'better-auth/client/plugins'
import { createAuthClient } from 'better-auth/react'

import type { Auth } from './index'
import { authEnv } from '../env'
import { childAuthClientPlugin } from './plugin/child/client'
import { switchUserClientPlugin } from './plugin/switch-user/client'

const env = authEnv()

export interface InitAuthClientOptions<TPlugins extends BetterAuthPlugin[] = BetterAuthPlugin[]> {
  baseUrl: string
  extraPlugins?: TPlugins
  fetchOptions?: RequestInit
}

export const initAuthClient = (options?: InitAuthClientOptions) =>
  ({
    baseURL: options?.baseUrl ?? env.NEXT_PUBLIC_ACCOUNT_URL,
    fetchOptions: options?.fetchOptions,
    plugins: [
      organizationClient({
        schema: inferOrgAdditionalFields<Auth>(),
        teams: { enabled: true },
      }),
      adminClient(),
      lastLoginMethodClient(),
      phoneNumberClient(),
      emailOTPClient(),
      jwtClient(),
      childAuthClientPlugin(),
      switchUserClientPlugin(),
      inferAdditionalFields<Auth>(),
      ...(options?.extraPlugins ?? []),
    ],
  }) satisfies BetterAuthClientOptions

const _authClient = createAuthClient(initAuthClient())

export type AuthClient = typeof _authClient

/**
 * Creates an auth client that talks to the API's /api/auth (e.g. apps/api).
 * Use this in frontends that should validate sessions against the main API
 * instead of the Account app.
 */
export function createApiAuthClient(baseUrl: string): AuthClient {
  return createAuthClient(
    initAuthClient({
      baseUrl,
      fetchOptions: { credentials: 'include' },
    }),
  )
}

export { idErrorMessage } from './utils/error-message'
